import * as React from "react";
import * as d3 from "d3";
import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";

const FamilyTreeComponent = ({ currentClone, clonesList }) => {
  const navigate = useNavigate();
  const svgRef = React.useRef();

  const buildFamilyTree = (cloneId, clones) => {
    if (!clones || !Array.isArray(clones)) return null;

    const cloneMap = new Map(
      clones.map((clone) => [clone._id, { ...clone.data, id: clone._id }])
    );

    const cloneRef = cloneMap.get(cloneId);
    if (!cloneRef) return null;

    const parent = cloneMap.get(cloneRef.child_of?.[0]?._id) || cloneRef;
    const grandParent = cloneMap.get(parent.child_of?.[0]?._id) || parent;

    const buildNode = (id) => {
      const clone = cloneMap.get(id);
      if (!clone) return null;

      const node = {
        id: clone.id,
        name: `${clone?.name} ${clone?.surname}`,
        picture: clone?.picture?.media || "",
        spousePicture: clone?.spouse && clone?.spouse[0]?.data?.picture?.media || "",
        spouseId: clone?.spouse && clone?.spouse[0]?._id || null,
        spouse: clone.spouse[0]?._id && clone.spouse?.map((spouse) => {
          const spouseClone = cloneMap.get(spouse._id);
          return spouseClone ? `${spouseClone.name} ${spouseClone.surname}` : null;
        }) || [],
        children: clones
          .filter((child) =>
            child?.data?.child_of?.some((parent) => parent._id === id)
          )
          .map((child) => buildNode(child._id)),
        child_of: clone.child_of?.map((parent) => {
          const parentClone = cloneMap.get(parent._id);
          return parentClone;
        }) || [],
      };

      return node;
    };

    return buildNode(grandParent?.id);
  };

  useEffect(() => {
    if (!currentClone?._id) return;

    const treeData = buildFamilyTree(currentClone?._id, clonesList);
    if (!treeData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const width = 1900;
    const height = 900;

    const root = d3.hierarchy(treeData, (d) => d.children);
    const treeLayout = d3.tree().size([width - 200, height - 200]);
    treeLayout(root);

    svg
      .selectAll(".link")
      .data(root.links())
      .enter()
      .append("line")
      .attr("class", "link")
      .attr("x1", (d) => d.source.x + 50)
      .attr("y1", (d) => d.source.y + 80)
      .attr("x2", (d) => d.target.x + 50)
      .attr("y2", (d) => d.target.y + 50)
      .attr("stroke", "#555");

    // Add main nodes for individuals.
    const node = svg
      .selectAll(".node")
      .data(root.descendants())
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", (d) => `translate(${d.x + 50},${d.y + 50})`)
      .on("click", (event, d) => {
        navigate(`/family-tree/${d.data.id}`);
      });

    node
      .append("rect")
      .attr("x", -45)
      .attr("y", -30)
      .attr("width", 90)
      .attr("height", 90)
      .attr("fill", "#fff")
      .attr("stroke", "#ccc")
      .attr("rx", 10)
      .attr("ry", 10);

    node.append("circle").attr("r", 20).attr("fill", "#D9DEE5");

    node
      .append("circle")
      .attr("r", 20)
      .attr("fill", (d) => (d.data.id === currentClone._id ? "#2C629B" : "#D9DEE5"))
      .attr("stroke", (d) => d.data.id === currentClone._id ? "#2C629B" : "none")
      .attr("strokeWidth", (d) => (d.data.id === currentClone._id ? 4 : 0));

    node
      .append("text")
      .attr("dy", ".35em")
      .attr("y", 34)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .each(function (d) {
        const name = d.data.name;
        const parts = name.split(" ");

        parts.forEach((part, index) => {
          d3.select(this)
            .append("tspan")
            .attr("x", 0)
            .attr("dy", index === 0 ? 0 : "1.1em")
            .text(part);
        });
      });


    const spouseNode = svg
      .selectAll(".spouse-node")
      .data(root.descendants().filter((d) => d.data.spouse && d.data.spouse.length))
      .enter()
      .append("g")
      .attr("class", "spouse-node")
      .attr("transform", (d) => `translate(${d.x + 170},${d.y + 50})`)
      .on("click", (event, d) => {
        if (d.data.spouse && d.data.spouse.length) {
          const spouseId = d?.data?.spouseId;
          if (spouseId) {
            navigate(`/family-tree/${spouseId}`);
          }
        }
      });


    spouseNode
      .append("rect")
      .attr("x", -45)
      .attr("y", -30)
      .attr("width", 90)
      .attr("height", 90)
      .attr("fill", "#fff")
      .attr("stroke", "#ccc")
      .attr("rx", 10)
      .attr("ry", 10);


    spouseNode.append("circle").attr("r", 20).attr("fill", "#D9DEE5");

    node
      .append("clipPath")
      .attr("id", (d) => `clip-${d.data.id}`)
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", 18);

    node
      .append("image")
      .attr("x", -25)
      .attr("y", -25)
      .attr("width", 50)
      .attr("height", 50)
      .attr("href", (d) => d.data.picture || "")
      .attr("clip-path", (d) => `url(#clip-${d.data.id})`);

    spouseNode
      .append("clipPath")
      .attr("id", (d) => `clip-${d.data.id}`)
      .append("circle")
      .attr("cx", 0)
      .attr("cy", 0)
      .attr("r", 10);

    spouseNode
      .append("image")
      .attr("x", -25)
      .attr("y", -25)
      .attr("width", 50)
      .attr("height", 50)
      .attr("href", (d) => d?.data?.spousePicture || "")
      .attr("clip-path", (d) => `url(#clip-${d.data.id})`);


    spouseNode
      .append("text")
      .attr("dy", ".35em")
      .attr("y", 32)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .attr("fill", "#555")
      // .text((d) => d.data.spouse.join(", "))
      .each(function (d) {
        const spouses = d.data.spouse || [];
        spouses.forEach((spouse, spouseIndex) => {
          if (spouse && typeof spouse === "string") {
            const nameParts = spouse.split(" ");
            nameParts.forEach((part, index) => {
              d3.select(this)
                .append("tspan")
                .attr("x", 0)
                .attr("dy", spouseIndex === 0 && index === 0 ? 0 : "1.1em")
                .text(part);
            });

            if (spouseIndex < spouses.length - 1) {
              d3.select(this).append("tspan").attr("x", 0).attr("dy", "1.1em").text("");
            }
          }
        });
      });



    node
      .filter((d) => d.data.spouse && d.data.spouse.length)
      .each(function (d) {
        d3.select(this)
          .append("line")
          .attr("x1", 45)
          .attr("y1", 0)
          .attr("x2", 90)
          .attr("y2", 0)
          .attr("stroke", "#555")
          .attr("strokeWidth", 1.5);

        const midpointX = (40 + 80) / 2;
        const midpointY = 0;

        d3.select(this)
          .append("text")
          .attr("x", midpointX)
          .attr("y", midpointY - 5)
          .attr("text-anchor", "middle")
          .attr("font-size", "16px")
          .attr("fill", "#b4173a")
          .text("❤");
      });

  }, [currentClone, clonesList]);

  return (
    <div className="container-family-tree">
      <svg
        ref={svgRef}
        className="family-tree-svg"
        width="100%"
        height="100%"
        viewBox="0 0 1900 650"
        preserveAspectRatio="xMidYMid meet"
      ></svg>
    </div>
  );
};

export default FamilyTreeComponent;